/**
 * CashoutDialog.js
 * Kassenschnitt-Dialog am Schichtende
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Banknote,
  Calculator,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Euro,
  Gift,
  TrendingUp,
  Clock,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CashoutDialog = ({ open, onClose, shift, driverId, onCashoutComplete }) => {
  const [step, setStep] = useState('count'); // count, confirm, success
  const [cashCounted, setCashCounted] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setCashCounted('');
      setNotes('');
      setStep('count');
      setResult(null);
    }
  }, [open]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours || 0);
    const m = Math.round(((hours || 0) - h) * 60);
    return `${h}h ${m}m`;
  };

  const expectedCash = (shift?.payments?.cash_collected || 0) + (shift?.payments?.tips_cash || 0);
  const countedCash = parseFloat(cashCounted) || 0;
  const difference = countedCash - expectedCash;

  const handleCalculate = () => {
    if (!cashCounted || countedCash < 0) {
      toast.error('Bitte gezähltes Bargeld eingeben');
      return;
    }
    setStep('confirm');
  };

  const handleEndShift = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/shift/end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cash_counted: countedCash,
          notes: notes
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setStep('success');
        toast.success('Schicht beendet! 🎉');
        setTimeout(() => {
          onCashoutComplete?.(data);
        }, 3000);
      } else {
        toast.error(data.detail || 'Fehler beim Beenden');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="h-6 w-6 text-primary" />
            Kassenschnitt
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Schicht beenden und abrechnen
          </DialogDescription>
        </DialogHeader>

        {/* Step: Count Cash */}
        {step === 'count' && (
          <div className="space-y-6 py-4">
            {/* Schicht-Zusammenfassung */}
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock className="h-4 w-4" />
                  <span>Schichtdauer</span>
                </div>
                <span className="text-white font-medium">
                  {formatDuration(shift?.duration_hours)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Package className="h-4 w-4" />
                  <span>Lieferungen</span>
                </div>
                <span className="text-white font-medium">
                  {shift?.deliveries?.completed || 0}
                </span>
              </div>
            </div>

            {/* Erwartetes Bargeld */}
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <p className="text-zinc-400 text-sm mb-1">Erwartetes Bargeld (Soll)</p>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(expectedCash)}
              </p>
              <div className="flex gap-4 mt-2 text-sm">
                <span className="text-zinc-500">
                  Bar: {formatCurrency(shift?.payments?.cash_collected)}
                </span>
                <span className="text-amber-500">
                  +Trink: {formatCurrency(shift?.payments?.tips_cash)}
                </span>
              </div>
            </div>

            {/* Bargeld zählen */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Banknote className="h-4 w-4 text-green-500" />
                Gezähltes Bargeld (Ist)
              </Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="number"
                  step="0.01"
                  value={cashCounted}
                  onChange={(e) => setCashCounted(e.target.value)}
                  className="pl-10 h-14 text-2xl font-bold bg-zinc-950 border-zinc-700 text-white"
                  placeholder="0.00"
                  autoFocus
                />
              </div>
            </div>

            {/* Notizen */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Notizen (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-zinc-950 border-zinc-700 text-white resize-none"
                placeholder="Anmerkungen zum Kassenschnitt..."
                rows={2}
              />
            </div>

            <Button
              onClick={handleCalculate}
              disabled={!cashCounted}
              className="w-full h-12 text-lg bg-primary hover:bg-primary/90"
            >
              <Calculator className="h-5 w-5 mr-2" />
              Berechnen
            </Button>
          </div>
        )}

        {/* Step: Confirm */}
        {step === 'confirm' && (
          <div className="space-y-6 py-4">
            {/* Differenz */}
            <div className={`rounded-lg p-4 border ${
              Math.abs(difference) < 0.01
                ? 'bg-green-900/30 border-green-700'
                : difference > 0
                ? 'bg-amber-900/30 border-amber-700'
                : 'bg-red-900/30 border-red-700'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {Math.abs(difference) < 0.01 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
                <span className="font-medium text-white">
                  {Math.abs(difference) < 0.01
                    ? 'Kasse stimmt!'
                    : difference > 0
                    ? `${formatCurrency(difference)} Überschuss`
                    : `${formatCurrency(Math.abs(difference))} Fehlbetrag`
                  }
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-zinc-400">Soll</p>
                  <p className="text-white font-medium">{formatCurrency(expectedCash)}</p>
                </div>
                <div>
                  <p className="text-zinc-400">Ist</p>
                  <p className="text-white font-medium">{formatCurrency(countedCash)}</p>
                </div>
              </div>
            </div>

            {/* Abzugeben vs Behalten */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-red-900/20 rounded-lg border border-red-700/50">
                <div className="flex items-center gap-2">
                  <Banknote className="h-5 w-5 text-red-500" />
                  <span className="text-white">Bar abzugeben</span>
                </div>
                <span className="text-red-400 font-bold text-xl">
                  {formatCurrency(shift?.payments?.cash_collected)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-900/20 rounded-lg border border-green-700/50">
                <div className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-green-500" />
                  <span className="text-white">Trinkgeld behalten</span>
                </div>
                <span className="text-green-400 font-bold text-xl">
                  {formatCurrency((shift?.payments?.tips_cash || 0) + (shift?.payments?.tips_card || 0))}
                </span>
              </div>
            </div>

            {/* Gesamtübersicht */}
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span className="font-medium text-white">Schicht-Übersicht</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Bar</span>
                  <span className="text-white">{formatCurrency(shift?.payments?.cash_collected)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">EC vor Ort</span>
                  <span className="text-white">{formatCurrency(shift?.payments?.card_onsite)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Online</span>
                  <span className="text-white">{formatCurrency(shift?.payments?.card_online)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-zinc-700">
                  <span className="text-zinc-400">Gesamt</span>
                  <span className="text-white font-bold">
                    {formatCurrency(
                      (shift?.payments?.cash_collected || 0) +
                      (shift?.payments?.card_onsite || 0) +
                      (shift?.payments?.card_online || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep('count')}
                variant="outline"
                className="flex-1 border-zinc-700"
              >
                Zurück
              </Button>
              <Button
                onClick={handleEndShift}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Schicht beenden'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && result && (
          <div className="py-8 text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-green-400 mb-2">
                Schicht beendet!
              </h3>
              <p className="text-zinc-400">
                {formatDuration(result.duration_hours)} gearbeitet
              </p>
            </div>

            <div className="bg-zinc-950 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-zinc-400">Lieferungen</span>
                <span className="text-white font-bold">{result.summary?.total_deliveries}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Umsatz</span>
                <span className="text-white font-bold">
                  {formatCurrency(
                    (result.summary?.cash_collected || 0) +
                    (result.summary?.card_onsite || 0) +
                    (result.summary?.card_online || 0)
                  )}
                </span>
              </div>
              <div className="flex justify-between text-amber-400">
                <span>Trinkgeld</span>
                <span className="font-bold">{formatCurrency(result.summary?.tips_total)}</span>
              </div>
            </div>

            <p className="text-zinc-500 text-sm">
              Du wirst automatisch weitergeleitet...
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CashoutDialog;
